'use client';

import Image from 'next/image';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/locales/translations';

type LocalizedText = {
  en: string;
  fr: string;
  ar: string;
};

const certImages = [
  '/assets/certifications/1.png',
  '/assets/certifications/2.png',
  '/assets/certifications/3.png',
  '/assets/certifications/4.png',
  '/assets/certifications/5.png',
];

const markets: Array<{
  country: LocalizedText;
  regulator: string;
  flag: string;
  bullets: Array<LocalizedText>;
}> = [
  {
    country: { en: 'Canada', fr: 'Canada', ar: 'كندا' },
    regulator: 'CFIA / ACIA',
    flag: '/assets/flags/canada.png',
    bullets: [
      {
        en: 'Aligned with Canadian food inspection requirements',
        fr: "Conforme aux exigences canadiennes d'inspection alimentaire",
        ar: 'متوافق مع متطلبات التفتيش الغذائي الكندية',
      },
      {
        en: 'Export documentation prepared for Canadian importers',
        fr: 'Documentation export préparée pour les importateurs canadiens',
        ar: 'وثائق التصدير معدة للمستوردين الكنديين',
      },
    ],
  },
  {
    country: { en: 'European Union', fr: 'Union Européenne', ar: 'الاتحاد الأوروبي' },
    regulator: 'DG SANTE',
    flag: '/assets/flags/European union.png',
    bullets: [
      {
        en: 'EU sanitary approval and traceability readiness',
        fr: 'Agrément sanitaire UE et traçabilité opérationnelle',
        ar: 'جاهزية للاعتماد الصحي الأوروبي والتتبع',
      },
      {
        en: 'Procedures structured for regulated EU seafood channels',
        fr: 'Procédures adaptées aux circuits européens réglementés',
        ar: 'إجراءات منظمة لقنوات المنتجات البحرية الأوروبية',
      },
    ],
  },
  {
    country: { en: 'China', fr: 'Chine', ar: 'الصين' },
    regulator: 'GACC / CIFER',
    flag: '/assets/flags/china.png',
    bullets: [
      {
        en: 'Market access support for GACC registration',
        fr: "Accompagnement pour l'enregistrement GACC",
        ar: 'دعم الوصول إلى السوق عبر تسجيل GACC',
      },
      {
        en: 'CIFER-focused facility and product compliance tracking',
        fr: 'Suivi de conformité des sites et produits via CIFER',
        ar: 'متابعة امتثال المنشآت والمنتجات عبر CIFER',
      },
    ],
  },
  {
    country: { en: 'Japan', fr: 'Japon', ar: 'اليابان' },
    regulator: 'MHLW',
    flag: '/assets/flags/japan.png',
    bullets: [
      {
        en: 'Controls aligned with Japanese health requirements',
        fr: 'Contrôles alignés sur les exigences sanitaires japonaises',
        ar: 'ضوابط متوافقة مع المتطلبات الصحية اليابانية',
      },
      {
        en: 'Export files prepared for precise buyer specifications',
        fr: 'Dossiers export adaptés aux spécifications acheteurs',
        ar: 'ملفات تصدير معدة وفق مواصفات المشترين',
      },
    ],
  },
  {
    country: { en: 'South Korea', fr: 'Corée du Sud', ar: 'كوريا الجنوبية' },
    regulator: 'MFDS',
    flag: '/assets/flags/south korea.jpg',
    bullets: [
      {
        en: 'Compliance pathway for Korean seafood import standards',
        fr: "Parcours de conformité aux normes coréennes d'importation",
        ar: 'مسار امتثال لمعايير استيراد المنتجات البحرية الكورية',
      },
      {
        en: 'Batch traceability and sanitary documentation support',
        fr: 'Traçabilité des lots et support documentaire sanitaire',
        ar: 'دعم تتبع الدفعات والوثائق الصحية',
      },
    ],
  },
];

const footerColumns = [
  {
    title: {
      en: 'Adapting To Market Requirements',
      fr: 'Adaptation aux Exigences des Marchés',
      ar: 'التكيف مع متطلبات الأسواق',
    },
    body: {
      en: 'We adjust documentation, traceability, labeling, and sanitary controls to match each destination market and buyer specification.',
      fr: "Nous adaptons la documentation, la traçabilité, l'étiquetage et les contrôles sanitaires aux exigences de chaque marché et acheteur.",
      ar: 'نقوم بتكييف الوثائق والتتبع والوسم والرقابة الصحية مع متطلبات كل سوق وكل مشتر.',
    },
  },
  {
    title: {
      en: 'Quality Commitment',
      fr: 'Engagement Qualité',
      ar: 'الالتزام بالجودة',
    },
    body: {
      en: 'Our commitment is simple: consistent product integrity, reliable export handling, and transparent compliance from landing to shipment.',
      fr: "Notre engagement est clair : intégrité constante des produits, gestion export fiable et conformité transparente de la réception à l'expédition.",
      ar: 'التزامنا واضح: سلامة منتج ثابتة، إدارة تصدير موثوقة، وامتثال شفاف من الاستلام إلى الشحن.',
    },
  },
];

export default function CertificationsSection() {
  const { language, dir } = useLanguage();
  const lang = language as 'en' | 'fr' | 'ar';
  const certTrans = translations[lang].certifications;

  return (
    <section id="certifications" className="py-24 px-6 md:px-12 bg-white" dir={dir}>
      <div className="max-w-7xl mx-auto">

        {/* Section Header */}
        <div className="text-center mb-14 reveal">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-foreground mb-5">
            {certTrans.title}
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            {certTrans.subtitle}
          </p>
        </div>

        {/* Certification Badges */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 mb-16">
          {certTrans.badges.map((badge, index) => (
            <div
              key={index}
              className="reveal bg-card border border-border rounded-2xl p-5 text-center shadow-sm flex flex-col items-center"
              style={{ transitionDelay: `${index * 70}ms` }}
            >
              <div className="relative w-24 h-24 md:w-28 md:h-28 mb-4">
                <Image
                  src={certImages[index]}
                  alt={badge.label}
                  fill
                  className="object-contain"
                  sizes="112px"
                  unoptimized
                />
              </div>
              <h3 className="text-sm font-bold text-foreground uppercase tracking-wide min-h-[2.5rem] flex items-center justify-center text-center">
                {badge.label}
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed mt-2 text-center">
                {badge.caption}
              </p>
            </div>
          ))}
        </div>

        {/* International Market Access */}
        <div className="reveal rounded-2xl overflow-hidden border border-border shadow-sm mb-14 bg-muted">
          <div className="px-6 py-4 text-center" style={{ backgroundColor: '#0B4F6C' }}>
            <h3 className="text-white font-bold text-xl md:text-2xl tracking-wide uppercase">
              {certTrans.marketTitle}
            </h3>
          </div>

          <div className="p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6">
              {markets.map((market, index) => {
                const placement =
                  index === 3
                    ? 'lg:col-start-2 lg:col-span-2'
                    : 'lg:col-span-2';

                return (
                  <div
                    key={market.regulator}
                    className={`bg-white border border-border rounded-2xl p-5 shadow-sm ${placement}`}
                  >
                    <div className="flex items-center gap-4 mb-4">
                      <div className="relative w-14 h-14 rounded-full overflow-hidden bg-muted flex-shrink-0">
                        <Image
                          src={market.flag}
                          alt={market.country[lang]}
                          fill
                          className="object-cover"
                          sizes="56px"
                          unoptimized
                        />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-foreground leading-tight">
                          {market.country[lang]}
                        </h4>
                        <p className="text-sm font-semibold text-primary mt-1">
                          {market.regulator}
                        </p>
                      </div>
                    </div>
                    <ul className="space-y-2">
                      {market.bullets.map((bullet, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                          <span className="leading-relaxed">{bullet[lang]}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer Columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 reveal">
          {footerColumns.map((column, index) => (
            <div key={index} className="bg-muted border border-border rounded-2xl p-6 md:p-8">
              <h3 className="font-serif text-2xl font-bold text-foreground mb-3">
                {column.title[lang]}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {column.body[lang]}
              </p>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}